import React from 'react';
import {
  Folder, FileText, FileImage, Film, Archive, Download, Trash2, ExternalLink,
} from 'lucide-react';
import type { UnifiedFileItem } from '../../types/storage';
import { storageApi } from '../../services/storageApi';

interface FileItemProps {
  item: UnifiedFileItem;
  onOpen: (item: UnifiedFileItem) => void;
  onDelete?: (item: UnifiedFileItem) => void;
  view?: 'grid' | 'list';
}

function formatBytes(bytes?: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function FileIcon({ mimeType, isFolder }: { mimeType?: string; isFolder: boolean }) {
  if (isFolder) return <Folder className="w-5 h-5 text-amber-400" />;
  if (mimeType?.startsWith('image/')) return <FileImage className="w-5 h-5 text-pink-400" />;
  if (mimeType?.startsWith('video/')) return <Film className="w-5 h-5 text-purple-400" />;
  if (mimeType?.includes('zip') || mimeType?.includes('archive'))
    return <Archive className="w-5 h-5 text-orange-400" />;
  return <FileText className="w-5 h-5 text-blue-400" />;
}

export default function FileItem({ item, onOpen, onDelete, view = 'list' }: FileItemProps) {
  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url =
      item.provider === 'google'
        ? storageApi.google.downloadFile(item.id)
        : storageApi.onedrive.downloadFile(item.id);
    window.open(url, '_blank');
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(item);
  };

  if (view === 'grid') {
    return (
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onOpen(item)}
        onClick={() => onOpen(item)}
        className="group flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50 cursor-pointer transition-all select-none"
      >
        <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-100 group-hover:border-indigo-100">
          <FileIcon mimeType={item.mimeType} isFolder={item.isFolder} />
        </div>
        <span className="text-xs font-medium text-gray-700 text-center line-clamp-2 w-full">
          {item.name}
        </span>
        <span className="text-xs text-gray-400">{formatBytes(item.size)}</span>
      </div>
    );
  }

  return (
    <div
      role="row"
      onClick={() => onOpen(item)}
      className="group flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
    >
      <FileIcon mimeType={item.mimeType} isFolder={item.isFolder} />
      <span className="flex-1 text-sm text-gray-800 truncate font-medium">{item.name}</span>
      <span className="hidden sm:block text-xs text-gray-400 w-20 text-right">
        {formatBytes(item.size)}
      </span>
      <span className="hidden md:block text-xs text-gray-400 w-28 text-right">
        {formatDate(item.modifiedAt)}
      </span>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {item.webUrl && (
          <a
            href={item.webUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700"
            title="Open in browser"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        {!item.isFolder && (
          <button
            onClick={handleDownload}
            className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700"
            title="Download"
          >
            <Download className="w-3.5 h-3.5" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={handleDelete}
            className="p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
